import { type InfiniteData } from '@tanstack/react-query';
import {
  type Comment,
  type CommentAuthor,
  CommentResolutionFilter,
  type CommentResolutionFilterOption,
  CommentVisibilityFilter,
  type CommentVisibilityFilterOption,
  type GetNodeCommentsResponse,
  type NodeComment,
} from '@/types/comments';
import { SortOrderOptions, SortOrderValues } from '@/types/sort';
import { KeyboardKey } from '@/lib/keyboard';

export const MAX_COMMENT_CHARACTERS = 4000;
export const DEFAULT_VISIBLE_REPLIES_COUNT = 3;
export const REPLIES_BATCH_SIZE = 10;
export const REPLIES_PAGE_SIZE = 10;
export const OPTIMISTIC_REPLY_ID_PREFIX = 'optimistic-reply-';

export const isOptimisticReplyId = (commentId: string) => commentId.startsWith(OPTIMISTIC_REPLY_ID_PREFIX);

export const normalizeCommentBody = (value: string) =>
  value
    .replace(/\r\n/g, '\n')
    .replace(/\r/g, '\n')
    .replace(/\u00a0/g, ' ');

export const moveCaretToEnd = (element: HTMLElement) => {
  const range = document.createRange();
  const selection = window.getSelection();

  if (!selection) {
    return;
  }

  range.selectNodeContents(element);
  range.collapse(false);
  selection.removeAllRanges();
  selection.addRange(range);
};

export const focusElementWithoutScroll = (element: HTMLElement) => {
  try {
    element.focus({ preventScroll: true });
  } catch {
    element.focus();
  }
};

export const insertTextAtCursor = (target: HTMLElement, text: string) => {
  const selection = window.getSelection();

  if (!selection || selection.rangeCount === 0) {
    target.textContent = `${target.textContent ?? ''}${text}`;
    moveCaretToEnd(target);
    return;
  }

  const range = selection.getRangeAt(0);

  if (!target.contains(range.commonAncestorContainer)) {
    target.textContent = `${target.textContent ?? ''}${text}`;
    moveCaretToEnd(target);
    return;
  }

  range.deleteContents();
  const textNode = document.createTextNode(text);
  range.insertNode(textNode);
  range.setStartAfter(textNode);
  range.collapse(true);
  selection.removeAllRanges();
  selection.addRange(range);
};

export const syncCommentDraftFromElement = ({
  element,
  setDraft,
}: {
  element: HTMLElement;
  setDraft: (draft: string) => void;
}) => {
  const nextValue = normalizeCommentBody(element.textContent ?? '');
  const nextDraft = nextValue.length > MAX_COMMENT_CHARACTERS ? nextValue.slice(0, MAX_COMMENT_CHARACTERS) : nextValue;

  if (nextDraft !== nextValue) {
    element.textContent = nextDraft;
    moveCaretToEnd(element);
  }

  setDraft(nextDraft);
  scrollCaretIntoView(element, { defer: true });

  return nextDraft;
};

export const handleCommentPaste = ({
  event,
  setDraft,
}: {
  event: {
    preventDefault: () => void;
    clipboardData: { getData: (type: string) => string };
    currentTarget: HTMLElement;
  };
  setDraft: (draft: string) => void;
}) => {
  event.preventDefault();

  const pastedText = normalizeCommentBody(event.clipboardData.getData('text/plain'));

  if (!pastedText) {
    return;
  }

  insertTextAtCursor(event.currentTarget, pastedText);

  syncCommentDraftFromElement({
    element: event.currentTarget,
    setDraft,
  });
};

export const resetCommentEditor = ({
  setDraft,
  editorRef,
}: {
  setDraft: (draft: string) => void;
  editorRef: { current: HTMLElement | null };
}) => {
  setDraft('');

  if (editorRef.current) {
    editorRef.current.textContent = '';
  }
};

export const syncCommentEditorValueIfBlurred = ({
  editorRef,
  nextValue,
}: {
  editorRef: { current: HTMLElement | null };
  nextValue: string;
}) => {
  if (!editorRef.current || document.activeElement === editorRef.current) {
    return;
  }

  if (editorRef.current.textContent !== nextValue) {
    editorRef.current.textContent = nextValue;
  }
};

export const handleCommentEditorKeyDown = ({
  event,
  onSubmit,
  onDismiss,
}: {
  event: {
    key: string;
    shiftKey: boolean;
    nativeEvent: { isComposing?: boolean };
    preventDefault: () => void;
  };
  onSubmit: () => void;
  onDismiss?: () => void;
}) => {
  if (event.key === KeyboardKey.Enter && !event.shiftKey && !event.nativeEvent.isComposing) {
    event.preventDefault();
    onSubmit();
    return;
  }

  if (event.key === KeyboardKey.Escape && onDismiss) {
    event.preventDefault();
    onDismiss();
  }
};

const scrollCaretIntoViewImmediate = (element: HTMLElement) => {
  const selection = window.getSelection();

  if (!selection || selection.rangeCount === 0) {
    element.scrollTop = element.scrollHeight;
    return;
  }

  const range = selection.getRangeAt(0);

  if (!element.contains(range.endContainer)) {
    element.scrollTop = element.scrollHeight;
    return;
  }

  const caretRect = range.getBoundingClientRect();
  const containerRect = element.getBoundingClientRect();

  if (!caretRect.height && !caretRect.width) {
    element.scrollTop = element.scrollHeight;
    return;
  }

  const caretTop = caretRect.top - containerRect.top + element.scrollTop;
  const caretBottom = caretTop + caretRect.height;
  const visibleBottom = element.scrollTop + element.clientHeight;

  if (caretTop < element.scrollTop) {
    element.scrollTop = caretTop;
    return;
  }

  if (caretBottom > visibleBottom) {
    element.scrollTop = caretBottom - element.clientHeight;
  }
};

export const scrollCaretIntoView = (element: HTMLElement, { defer = false }: { defer?: boolean } = {}) => {
  if (defer) {
    requestAnimationFrame(() => {
      scrollCaretIntoViewImmediate(element);
    });
    return;
  }

  scrollCaretIntoViewImmediate(element);
};

type ScrollElementIntoViewOptions = {
  behavior?: ScrollBehavior;
  marginTop?: number;
  marginBottom?: number;
  defer?: boolean;
  container?: HTMLElement | null;
};

const scrollElementIntoContainer = (
  element: HTMLElement,
  {
    behavior = 'smooth',
    marginTop = 0,
    marginBottom = 0,
    container = null,
  }: Omit<ScrollElementIntoViewOptions, 'defer'> = {},
) => {
  if (!container) {
    element.scrollIntoView({ behavior, block: 'nearest', inline: 'nearest' });
    return true;
  }

  const elementRect = element.getBoundingClientRect();
  const containerRect = container.getBoundingClientRect();

  const overTopBy = elementRect.top - containerRect.top - marginTop;
  const overBottomBy = elementRect.bottom - containerRect.bottom + marginBottom;

  if (overTopBy < 0) {
    container.scrollBy({ top: overTopBy, behavior });
    return false;
  }

  if (overBottomBy > 0) {
    container.scrollBy({ top: overBottomBy, behavior });
    return false;
  }

  return true;
};

export const scrollElementIntoViewOnNextFrame = (
  element: HTMLElement,
  { defer = true, ...options }: ScrollElementIntoViewOptions = {},
) => {
  const scroll = () => {
    if (typeof window === 'undefined') {
      return;
    }

    scrollElementIntoContainer(element, options);
  };

  if (defer) {
    requestAnimationFrame(scroll);
    return;
  }

  scroll();
};

type ReplyLike = {
  id: string;
  createdAt: string;
};

const getTimestamp = (value: string): number | null => {
  const timestamp = Date.parse(value);
  return Number.isNaN(timestamp) ? null : timestamp;
};

export const sortRepliesChronologically = <T extends ReplyLike>(replies: Array<T>) =>
  [...replies].sort((leftReply, rightReply) => {
    const leftTs = getTimestamp(leftReply.createdAt);
    const rightTs = getTimestamp(rightReply.createdAt);

    if (leftTs === null && rightTs !== null) {
      return 1;
    }
    if (leftTs !== null && rightTs === null) {
      return -1;
    }
    if (leftTs !== null && rightTs !== null && leftTs !== rightTs) {
      return leftTs - rightTs;
    }
    return leftReply.id.localeCompare(rightReply.id);
  });

export const mergeRepliesById = <T extends ReplyLike>(...replyCollections: Array<Array<T>>) => {
  const repliesById = new Map<string, T>();

  replyCollections.flat().forEach((reply) => {
    repliesById.set(reply.id, reply);
  });

  return sortRepliesChronologically(Array.from(repliesById.values()));
};

export const getVisibleRepliesState = <T extends ReplyLike>({
  replies,
  repliesCount,
  visibleRepliesCount,
  loadMoreBatchSize,
}: {
  replies: Array<T>;
  repliesCount: number;
  visibleRepliesCount: number;
  loadMoreBatchSize: number;
}) => {
  // If the total amount of replies is the same as the length of the replies array, it means all the comments are displayed. In that
  // case, we don't want to add replies to the collapsed section.
  const repliesToShow =
    repliesCount === replies.length ? Math.max(replies.length, visibleRepliesCount) : visibleRepliesCount;
  const safeVisibleRepliesCount = Math.max(1, repliesToShow);
  const safeLoadMoreBatchSize = Math.max(1, loadMoreBatchSize);
  const orderedReplies = sortRepliesChronologically(replies);
  const visibleReplies = orderedReplies.slice(-safeVisibleRepliesCount);
  const hiddenRepliesCount = Math.max(repliesCount - visibleReplies.length, 0);
  const nextLoadRepliesCount = Math.min(hiddenRepliesCount, safeLoadMoreBatchSize);

  return {
    visibleReplies,
    hiddenRepliesCount,
    nextLoadRepliesCount,
    shouldShowMoreRepliesAction: nextLoadRepliesCount > 0,
  };
};

export const shouldInsertNewComment = ({
  sortOrder,
  sortValue,
  hasNextPage,
}: {
  sortOrder: string;
  sortValue: string;
  hasNextPage: boolean;
}): 'top' | 'bottom' | 'none' => {
  if (sortValue !== SortOrderValues.CREATED_AT) {
    return 'none';
  }

  if (sortOrder === SortOrderOptions.DESC) {
    return 'top';
  }

  if (sortOrder === SortOrderOptions.ASC) {
    if (!hasNextPage) {
      return 'bottom';
    }
    // If there are more pages, do nothing (comment is beyond current view)
    return 'none';
  }

  return 'none';
};

// Derives hasNextPage for a specific cached page (rather than the live hook's own
// hasNextPage prop) so each differently-sorted/paginated cached query gets its own
// correct insert position, matching the query's own last-page pagination state.
export const resolveInsertPositionForPagedQuery = ({
  oldData,
  sortOrder,
  sortValue,
}: {
  oldData: InfiniteData<GetNodeCommentsResponse>;
  sortOrder: string;
  sortValue: string;
}): 'top' | 'bottom' | 'none' => {
  const lastPage = oldData.pages[oldData.pages.length - 1];
  const hasNextPage = Boolean(
    lastPage && lastPage.pagination.offset + lastPage.pagination.limit < lastPage.pagination.count,
  );

  return shouldInsertNewComment({ sortOrder, sortValue, hasNextPage });
};

export const insertCommentIntoCache = ({
  oldData,
  newNodeComment,
  insertPosition,
}: {
  oldData: InfiniteData<GetNodeCommentsResponse>;
  newNodeComment: NodeComment;
  insertPosition: 'top' | 'bottom' | 'none';
}): InfiniteData<GetNodeCommentsResponse> => {
  if (insertPosition === 'none') {
    // Don't insert the comment, just return the data as-is
    return oldData;
  }

  if (insertPosition === 'top') {
    // Add the new comment to the first page
    return {
      ...oldData,
      pages: oldData.pages.map((page, index) =>
        index === 0
          ? {
              ...page,
              comments: [newNodeComment, ...page.comments],
              pagination: {
                ...page.pagination,
                count: page.pagination.count + 1,
              },
            }
          : page,
      ),
    };
  }

  if (insertPosition === 'bottom') {
    // Add the new comment to the last page
    const lastPageIndex = oldData.pages.length - 1;
    return {
      ...oldData,
      pages: oldData.pages.map((page, index) =>
        index === lastPageIndex
          ? {
              ...page,
              comments: [...page.comments, newNodeComment],
              pagination: {
                ...page.pagination,
                count: page.pagination.count + 1,
              },
            }
          : page,
      ),
    };
  }

  return oldData;
};

const TOMBSTONE_MARKER = { isDeleted: true } as const;

const setCommentTombstone = <T extends Comment>(comment: T): T => ({
  ...comment,
  tombstone: TOMBSTONE_MARKER,
});

export const applyDeletedCommentTombstoneToCache = ({
  oldData,
  commentId,
  parentCommentId,
}: {
  oldData: InfiniteData<GetNodeCommentsResponse>;
  commentId: string;
  parentCommentId?: string | null;
}) => {
  let didUpdateAnyPage = false;

  const nextPages = oldData.pages.map((page) => {
    let didUpdatePage = false;

    const nextComments = page.comments.map((comment) => {
      if (comment.commentId === commentId) {
        if (
          comment.tombstone?.isDeleted &&
          comment.comment.tombstone?.isDeleted &&
          comment.latestReplies.length === 0 &&
          comment.repliesCount === 0
        ) {
          return comment;
        }

        didUpdatePage = true;

        return {
          ...comment,
          tombstone: TOMBSTONE_MARKER,
          comment: setCommentTombstone(comment.comment),
          latestReplies: [],
          repliesCount: 0,
        };
      }

      const shouldPatchReplies = parentCommentId
        ? comment.commentId === parentCommentId
        : comment.latestReplies.some((reply) => reply.id === commentId);

      if (!shouldPatchReplies) {
        return comment;
      }

      const deletedReplyIds = comment.deletedReplyIds ?? [];
      if (deletedReplyIds.includes(commentId)) {
        return comment;
      }

      let didUpdateReplies = false;
      const nextLatestReplies = comment.latestReplies.map((reply) => {
        if (reply.id !== commentId || reply.tombstone?.isDeleted) {
          return reply;
        }

        didUpdateReplies = true;
        return setCommentTombstone(reply);
      });

      if (didUpdateReplies) {
        didUpdatePage = true;

        return {
          ...comment,
          latestReplies: nextLatestReplies,
          deletedReplyIds: [...deletedReplyIds, commentId],
        };
      }

      // Hidden reply delete: keep loaded rows untouched, but correct the hidden count.
      // This prevents stale "X more replies" when the deleted reply was not loaded yet.
      if (parentCommentId) {
        const nextRepliesCount = Math.max(comment.latestReplies.length, comment.repliesCount - 1);

        didUpdatePage = true;

        return {
          ...comment,
          repliesCount: nextRepliesCount,
          deletedReplyIds: [...deletedReplyIds, commentId],
        };
      }

      return comment;
    });

    if (!didUpdatePage) {
      return page;
    }

    didUpdateAnyPage = true;

    return {
      ...page,
      comments: nextComments,
    };
  });

  if (!didUpdateAnyPage) {
    return oldData;
  }

  return {
    ...oldData,
    pages: nextPages,
  };
};

export const applyCommentResolutionUpdateToCache = ({
  oldData,
  commentId,
  resolvedAt,
  resolvedBy,
  resolutionFilter,
}: {
  oldData: InfiniteData<GetNodeCommentsResponse>;
  commentId: string;
  resolvedAt: string | null;
  resolvedBy: CommentAuthor | null;
  resolutionFilter?: CommentResolutionFilterOption;
}) => {
  let didUpdateAnyPage = false;

  const shouldKeepComment =
    resolutionFilter === CommentResolutionFilter.RESOLVED
      ? Boolean(resolvedAt)
      : resolutionFilter === CommentResolutionFilter.UNRESOLVED
        ? !resolvedAt
        : true;

  const nextPages = oldData.pages.map((page) => {
    let didUpdatePage = false;
    let removedComment = false;

    const nextComments: Array<NodeComment> = [];

    page.comments.forEach((comment) => {
      if (comment.commentId !== commentId) {
        nextComments.push(comment);
        return;
      }

      didUpdatePage = true;

      if (!shouldKeepComment) {
        removedComment = true;
        return;
      }

      nextComments.push({
        ...comment,
        resolvedAt,
        resolvedBy,
      });
    });

    if (!didUpdatePage) {
      return page;
    }

    didUpdateAnyPage = true;

    return {
      ...page,
      comments: nextComments,
      pagination: removedComment
        ? {
            ...page.pagination,
            count: Math.max(page.pagination.count - 1, 0),
          }
        : page.pagination,
    };
  });

  if (!didUpdateAnyPage) {
    return oldData;
  }

  return {
    ...oldData,
    pages: nextPages,
  };
};

// Only root comments carry the private flag — replies are never private, so a reply
// update should never be evaluated against the visibility filter.
export const commentMatchesVisibilityFilter = (
  isPrivate: boolean | null | undefined,
  visibilityFilter: CommentVisibilityFilterOption | undefined,
): boolean => {
  if (visibilityFilter === CommentVisibilityFilter.PRIVATE) {
    return Boolean(isPrivate);
  }

  if (visibilityFilter === CommentVisibilityFilter.PUBLIC) {
    return !isPrivate;
  }

  return true;
};

export const removeNodeCommentFromCache = ({
  oldData,
  commentId,
}: {
  oldData: InfiniteData<GetNodeCommentsResponse>;
  commentId: string;
}) => {
  let didUpdateAnyPage = false;

  const nextPages = oldData.pages.map((page) => {
    const hasMatch = page.comments.some((comment) => comment.commentId === commentId);

    if (!hasMatch) {
      return page;
    }

    didUpdateAnyPage = true;

    return {
      ...page,
      comments: page.comments.filter((comment) => comment.commentId !== commentId),
      pagination: {
        ...page.pagination,
        count: Math.max(page.pagination.count - 1, 0),
      },
    };
  });

  if (!didUpdateAnyPage) {
    return oldData;
  }

  return {
    ...oldData,
    pages: nextPages,
  };
};
